import Vue from 'vue';
import { mapGetters, mapActions } from 'vuex';
import { BfModal, BfDialog, BfTab, BfTabs, BfDivider, BfButton, BfTextInput } from 'src/components/Butterfly';
import Multiselect from 'vue-multiselect';
import Project from 'src/api/project';
import _ from 'lodash';
import ClipboardJS from 'clipboard';
import isEmail from 'validator/lib/isEmail';
import { CLEAR_ERRORS } from 'src/store/types';
export default Vue.extend({
    components: { Multiselect, BfModal, BfDialog, BfTabs, BfTab, BfDivider, BfButton, BfTextInput },
    props: {
        visible: { type: Boolean, default: false, required: false },
        approvedDomains: { type: Array, required: false, default: () => [] },
        dashboard: { type: Object, required: true },
        dashboardMembers: { type: Array, required: true },
        projectMembers: { type: Array, required: true },
        shareableLink: { type: String, required: false, default: null }
    },
    data() {
        return {
            search: '',
            inviting: false,
            tooltip: false,
            users: [],
            copyText: 'Copy',
            clipboard: null,
            isSearching: false,
            serverErrors: [],
            userStatusMessage: '',
            userStatusTimer: null,
            stagedUser: null,
            removingUser: null,
            fetchUsers: _.debounce(search => {
                this.search = search;
                if (!search)
                    return;
                const isQueryEmail = isEmail(search);
                this.isSearching = true;
                Project.searchDashboardUsers(this.dashboard.id, search)
                    .then((response) => {
                    if (isQueryEmail === true) {
                        // Is there a perfect match for the email in the results?
                        let match = response
                            .concat(this.projectMembers)
                            .concat(this.dashboardMembers)
                            .find(user => user.email.toLowerCase() === search.toLowerCase());
                        if (!match) {
                            // Offer the option to invite by email
                            this.users = [{ email: search, id: -1 }].concat(response);
                        }
                        else {
                            this.users = response;
                        }
                    }
                    else {
                        this.users = response;
                    }
                    this.isSearching = false;
                });
            }, 300),
        };
    },
    computed: {
        ...mapGetters([
            'currentSite', 'currentUser', 'viewerUserLimit', 'viewerMembershipCount',
        ]),
        validStagedUser() {
            return this.stagedUser && isEmail(this.stagedUser.email);
        },
        viewerLimitMessage() {
            const remaining = this.viewerUserLimit - this.viewerMembershipCount;
            if (!Number.isInteger(remaining)) {
                return '';
            }
            return `${this.viewerUserLimit - this.viewerMembershipCount} explorer seats remain`;
        },
    },
    watch: {
        visible(value) {
            this.setUpClipboard(value);
        }
    },
    methods: {
        ...mapActions({ CLEAR_ERRORS }),
        beforeDestroy() {
            // See ch22034 and https://github.com/shentao/vue-multiselect/issues/725
            this.$refs.multiselect?.deactivate();
        },
        onSelectClose() {
            const isQueryEmail = isEmail(this.search || '');
            // An email has been entered, select the invitation option
            if (this.users[0]?.id === -1) {
                this.stageUser(this.users[0]);
                // Input is no longer a valid email, clear the selection
            }
            else if (this.stagedUser?.id === -1 && !isQueryEmail) {
                this.stageUser(null);
            }
        },
        setUpClipboard(visible) {
            if (this.clipboard !== null)
                this.clipboard.destroy();
            if (!visible) {
                // Dont rebind clipboard triggers if the modal is not visible
                return;
            }
            this.$nextTick(() => {
                const triggers = this.$el.querySelectorAll('button.copy-button,div.copy-text');
                this.clipboard = new ClipboardJS(triggers, {
                    target: (trigger) => trigger,
                    text: () => this.shareableLink
                });
                this.clipboard.on('success', () => {
                    this.copyText = 'Copied!';
                    window.setTimeout(() => {
                        this.copyText = 'Copy';
                    }, 1500);
                    this.$emit('linkCopied');
                });
                this.clipboard.on('error', () => {
                    this.copyText = 'Copy Failed';
                    window.setTimeout(() => {
                        this.copyText = 'Copy';
                    }, 1500);
                });
            });
        },
        tabChanged(index) {
            // if the modal is visible & it's the first tab then we
            // want clipboard.js setup, otherwise just tear down
            this.setUpClipboard(this.visible && index === 0);
        },
        close() {
            this.stagedUser = null;
            this.serverErrors = [];
            this.$emit('close');
        },
        stageUser(user) {
            this.stagedUser = user;
        },
        addUser() {
            const user = this.stagedUser;
            this.inviting = true;
            this.serverErrors = [];
            // Is this an invite or an add?
            if (user.id === -1) {
                Project.inviteToDashboard(this.dashboard.id, user.email, this.shareableLink)
                    .then(() => {
                    this.$emit('updated', this.dashboardMembers.concat(user));
                    this.setUserStatus(`${user.email} has been invited`);
                    this.inviting = false;
                    this.stagedUser = null;
                    this.$analytics.track.dashboard.inviteUser(user.email, this.dashboard.id, this.currentUser.email);
                })
                    .catch(errors => {
                    this.CLEAR_ERRORS();
                    this.serverErrors = typeof errors.body === 'object' ? Object.values(errors.body).flat() : [errors.body];
                    this.inviting = false;
                });
            }
            else {
                Project.addUserToDashboard(this.dashboard.id, user, this.shareableLink)
                    .then(response => {
                    this.$emit('updated', this.dashboardMembers.concat(user));
                    this.setUserStatus(`${response.user.full_name} has been added`);
                    this.users = [];
                    this.inviting = false;
                    this.stagedUser = null;
                    this.$analytics.track.dashboard.addUser(user.email, this.dashboard.id, this.currentUser.email);
                })
                    .catch(errors => {
                    this.CLEAR_ERRORS();
                    this.serverErrors = typeof errors.body === 'object' ? Object.values(errors.body).flat() : [errors.body];
                    this.inviting = false;
                });
            }
        },
        removeMember(id) {
            this.removingUser = id;
            Project.deleteUserFromDashboard(this.dashboard.id, id)
                .then(() => {
                this.removingUser = null;
                this.$emit('updated', this.dashboardMembers.filter(item => item.id !== id));
            })
                .catch(errors => {
                this.removingUser = null;
            });
        },
        userLabel(user) {
            if (user.id === -1) {
                // This must be the invite option.
                return `Send invite to ${user.email}`;
            }
            else {
                return `${user.full_name} (${user.email})`;
            }
        },
        limitText(count) {
            return `and ${count} other users`;
        },
        setUserStatus(text) {
            this.userStatusMessage = text;
            if (this.userStatusTimer) {
                window.clearTimeout(this.userStatusTimer);
            }
            this.userStatusTimer = window.setTimeout(() => {
                this.userStatusMessage = '';
            }, 2000);
        },
    }
});
